
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { Order, User, CoinTransaction, Store, ReturnRequest, VIPConfig } from '../types';
import { ShoppingBag, Package, ChevronRight, Home, Clock, Coins, ArrowUpRight, ArrowDownLeft, Wallet, Star, X, MapPin, CreditCard, Tag, RotateCcw, AlertCircle, Loader2, CheckCircle2, Award, Zap, Sparkles } from 'lucide-react';
import OrderTimeline from '../components/OrderTimeline';
import { orderService } from '../services/orderService';
import { vipService } from '../services/vipService';
import { DEFAULT_STORE_ID } from '../constants';
import VIPBadge from '../components/VIPBadge';

interface CustomerOrdersProps {
  user: User | null;
  onNavigateHome: () => void;
}

const CustomerOrders: React.FC<CustomerOrdersProps> = ({ user, onNavigateHome }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [coinHistory, setCoinHistory] = useState<CoinTransaction[]>([]);
  const [activeTab, setActiveTab] = useState<'orders' | 'loyalty' | 'vip'>('orders');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  
  // VIP States
  const [currentVIP, setCurrentVIP] = useState<VIPConfig | null>(null);
  const [nextVIP, setNextVIP] = useState<any>(null);
  const [totalSpent, setTotalSpent] = useState(0);

  // Return States
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);

  const userId = user?.id || 'guest-session';

  useEffect(() => {
    refreshData();
  }, [userId]);

  const refreshData = () => {
    const all = storage.getOrders();
    const s = storage.getStores();
    setOrders(all.filter(o => o.userId === userId).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    setCoinHistory(storage.getCoinTransactions(userId).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
    setStores(s);

    // Load VIP Info for main store
    const spent = vipService.getCustomerSpend(DEFAULT_STORE_ID, userId);
    setTotalSpent(spent);
    setCurrentVIP(vipService.getCurrentTier(DEFAULT_STORE_ID, userId));
    setNextVIP(vipService.getNextTierInfo(DEFAULT_STORE_ID, userId));
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'DELIVERED': return 'text-green-600 bg-green-50';
      case 'SHIPPED': return 'text-blue-600 bg-blue-50';
      case 'PROCESSING': return 'text-purple-600 bg-purple-50';
      case 'PENDING': return 'text-orange-600 bg-orange-50';
      case 'RETURN_REQUESTED': return 'text-amber-600 bg-amber-50';
      case 'RETURNED': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleInitiateReturn = async () => {
    if (!selectedOrder || !returnReason) return;
    setIsSubmittingReturn(true);
    try {
      await orderService.requestReturn(selectedOrder.id, returnReason);
      setIsReturnModalOpen(false);
      setReturnReason('');
      setSelectedOrder(null);
      refreshData();
      alert("Return request submitted successfully! Merchant will review it.");
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 space-y-10">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">Account Dashboard</h1>
          <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mt-1">Tech Enthusiast Since 2024</p>
        </div>
        <button onClick={onNavigateHome} className="flex items-center gap-2 text-sm font-black text-blue-600 bg-blue-50 px-6 py-3 rounded-2xl hover:bg-blue-100 transition-all">
          <Home className="h-4 w-4" /> Continue Shopping
        </button>
      </div>

      {/* VIP & Spend Stats Card */}
      <div className="bg-gray-950 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden group">
         <div className="absolute inset-0 bg-blue-600/5 backdrop-blur-3xl"></div>
         <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:scale-110 transition-transform"><Award className="h-40 w-40" /></div>
         
         <div className="relative flex flex-col md:flex-row justify-between items-center gap-10">
            <div className="space-y-6 flex-grow text-center md:text-left">
               <div className="flex flex-col md:flex-row items-center gap-4">
                  <h3 className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">Current Standing</h3>
                  <VIPBadge config={currentVIP} size="lg" />
                  {!currentVIP && <span className="bg-white/10 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-400 border border-white/5">Standard Member</span>}
               </div>
               <div>
                  <p className="text-5xl font-black tracking-tighter">${totalSpent.toLocaleString()} <span className="text-sm text-blue-400 font-bold tracking-widest uppercase ml-2">Lifetime Spend</span></p>
               </div>
               
               {nextVIP ? (
                  <div className="space-y-3 max-w-sm mx-auto md:mx-0">
                     <div className="flex justify-between items-end text-[10px] font-black uppercase tracking-widest">
                        <span className="text-blue-400">Progress to {nextVIP.tier.label}</span>
                        <span>${nextVIP.remaining.toLocaleString()} left</span>
                     </div>
                     <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600 transition-all duration-1000 shadow-[0_0_15px_rgba(37,99,235,0.5)]" style={{ width: `${nextVIP.progress}%` }}></div>
                     </div>
                  </div>
               ) : (
                  <div className="flex items-center gap-2 text-emerald-400 font-black text-xs uppercase tracking-widest">
                     <Sparkles className="h-4 w-4" /> You've reached the highest tier!
                  </div>
               )}
            </div>

            <div className="shrink-0 bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-[2.5rem] text-center min-w-[220px]">
               <p className="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-1">VIP Privilege</p>
               <h4 className="text-3xl font-black">-{currentVIP?.discountPercentage || 0}%</h4>
               <p className="text-[9px] text-gray-500 font-bold mt-2 uppercase">Permanent discount applied</p>
            </div>
         </div>
      </div>

      <div className="space-y-6">
        {/* Tab Selector */}
        <div className="flex gap-4 p-1.5 bg-gray-100 w-fit rounded-2xl border border-gray-100">
           <button 
             onClick={() => setActiveTab('orders')}
             className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'orders' ? 'bg-white text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-900'}`}
           >
             Orders
           </button>
           <button 
             onClick={() => setActiveTab('loyalty')}
             className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'loyalty' ? 'bg-white text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-900'}`}
           >
             Lynq Coins
           </button>
           <button 
             onClick={() => setActiveTab('vip')}
             className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'vip' ? 'bg-white text-gray-900 shadow-lg' : 'text-gray-400 hover:text-gray-900'}`}
           >
             VIP Perks
           </button>
        </div>

        {activeTab === 'orders' && (
          <div className="space-y-4 animate-in fade-in duration-500">
            {orders.length === 0 ? (
              <div className="text-center py-24 bg-white rounded-[3rem] border border-dashed border-gray-200">
                <ShoppingBag className="h-16 w-16 text-gray-200 mx-auto mb-6" />
                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">You haven't placed any orders yet.</p>
              </div>
            ) : (
              orders.map((order) => (
                <div 
                  key={order.id} 
                  onClick={() => setSelectedOrder(order)}
                  className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6 group hover:border-blue-200 hover:shadow-xl hover:shadow-blue-50/50 transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-6">
                    <div className="p-4 bg-gray-50 rounded-2xl text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                      <Package className="h-8 w-8" />
                    </div>
                    <div>
                      <h3 className="font-black text-xl text-gray-900 tracking-tight">Order #{order.id}</h3>
                      <div className="flex items-center gap-4 text-xs text-gray-400 mt-1 font-medium">
                        <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {new Date(order.createdAt).toLocaleDateString()}</span>
                        <span>•</span>
                        <span className="font-black text-gray-900 text-sm">${order.total.toFixed(2)}</span>
                        {order.vipTierAtPurchase && (
                          <span className="text-blue-600 font-black uppercase text-[8px] border border-blue-100 px-1.5 rounded-md">VIP Order</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <span className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.status)}`}>
                      {order.status.replace('_', ' ')}
                    </span>
                    <ChevronRight className="h-6 w-6 text-gray-200 group-hover:text-blue-600 transition-colors" />
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'vip' && (
          <div className="space-y-8 animate-in fade-in duration-500">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {vipService.getStoreVIPConfigs(DEFAULT_STORE_ID).map(tier => (
                  <div key={tier.level} className={`p-8 rounded-[3rem] border-2 transition-all relative overflow-hidden ${
                    currentVIP?.level === tier.level ? 'border-blue-600 bg-blue-50/50 shadow-xl' : 'border-gray-100 bg-white opacity-80'
                  }`}>
                     {currentVIP?.level === tier.level && (
                       <div className="absolute top-0 right-0 p-6 opacity-10 rotate-12"><CheckCircle2 className="h-20 w-20 text-blue-600" /></div>
                     )}
                     <div className="relative space-y-6">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: tier.color }}>
                           <Award className="h-8 w-8" />
                        </div>
                        <div>
                           <h4 className="text-2xl font-black text-gray-900">{tier.label}</h4>
                           <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Status Level</p>
                        </div>
                        <ul className="space-y-4 pt-4 border-t border-gray-100">
                           <li className="flex items-center gap-3 text-xs font-bold text-gray-700">
                              <Zap className="h-4 w-4 text-blue-500" /> Permanent {tier.discountPercentage}% Discount
                           </li>
                           <li className="flex items-center gap-3 text-xs font-bold text-gray-700">
                              <Star className="h-4 w-4 text-amber-500" /> Custom Profile Badge
                           </li>
                        </ul>
                        <div className="pt-4">
                           <p className="text-[10px] text-gray-400 font-black uppercase">Benchmark: ${tier.spendThreshold}</p>
                        </div>
                     </div>
                  </div>
                ))}
             </div>
             
             <div className="p-10 bg-blue-600 rounded-[3rem] text-white flex flex-col md:flex-row items-center justify-between gap-10 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                <div className="relative space-y-2 text-center md:text-left">
                   <h3 className="text-2xl font-black">Elite Status Concierge</h3>
                   <p className="text-blue-100 font-medium">As a VIP member, you get priority chat support and exclusive early access to all new tech drops.</p>
                </div>
                <button className="relative bg-white text-blue-600 px-10 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:scale-105 active:scale-95 transition-all">
                   Contact VIP Support
                </button>
             </div>
          </div>
        )}

        {activeTab === 'loyalty' && (
          <div className="space-y-6 animate-in fade-in duration-500">
             {/* Loyalty Status Card */}
             <div className="bg-gradient-to-r from-orange-600 to-amber-500 p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative flex flex-col sm:flex-row justify-between items-center gap-10">
                   <div className="space-y-4">
                      <div className="flex items-center gap-2 bg-white/20 px-4 py-1.5 rounded-full w-fit">
                         <Star className="h-4 w-4 fill-current" />
                         <span className="text-[10px] font-black uppercase tracking-widest">Elite Member Status</span>
                      </div>
                      <h2 className="text-4xl font-black tracking-tight">Unlock Premium Tech <br/>With Every Byte.</h2>
                      <p className="text-orange-100 text-sm font-medium max-w-sm">Use your coins at checkout to get instant discounts on the latest gadgets.</p>
                   </div>
                   <div className="text-center bg-white/10 backdrop-blur-xl p-8 rounded-[2.5rem] border border-white/20 min-w-[200px]">
                      <p className="text-[10px] font-black uppercase tracking-widest mb-1 opacity-70">Redemption Value</p>
                      <h4 className="text-3xl font-black text-white">${((user?.coins || 0) * 0.1).toFixed(2)}</h4>
                      <p className="text-[10px] font-bold mt-2">10 Coins = $1.00</p>
                   </div>
                </div>
             </div>

             <div className="bg-white rounded-[3rem] border border-gray-100 shadow-xl overflow-hidden">
                <div className="p-10 border-b border-gray-50 flex items-center gap-3">
                   <Wallet className="h-6 w-6 text-orange-600" />
                   <h3 className="text-2xl font-black text-gray-900">Transaction History</h3>
                </div>
                <div className="divide-y divide-gray-50">
                   {coinHistory.length === 0 ? (
                     <div className="p-20 text-center text-gray-400 font-bold uppercase tracking-widest text-xs">No transactions yet.</div>
                   ) : (
                     coinHistory.map(tx => (
                       <div key={tx.id} className="p-8 flex items-center justify-between hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-6">
                             <div className={`p-4 rounded-2xl ${tx.amount > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                {tx.amount > 0 ? <ArrowUpRight className="h-6 w-6" /> : <ArrowDownLeft className="h-6 w-6" />}
                             </div>
                             <div>
                                <p className="font-black text-gray-900">{tx.description}</p>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                   {new Date(tx.timestamp).toLocaleString()} • REF: {tx.id}
                                </p>
                             </div>
                          </div>
                          <div className={`text-xl font-black ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                             {tx.amount > 0 ? '+' : ''}{tx.amount} <span className="text-[10px] uppercase tracking-widest">Coins</span>
                          </div>
                       </div>
                     ))
                   )}
                </div>
             </div>
          </div>
        )}
      </div>

      {/* Order Detail & Tracking Modal (Existing) */}
      {selectedOrder && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-5xl h-[85vh] rounded-[40px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-20 duration-500 flex flex-col">
              {/* Header */}
              <div className="p-8 border-b border-gray-100 flex justify-between items-center shrink-0 bg-gray-50/50">
                 <div>
                    <h3 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                       <Package className="h-6 w-6 text-blue-600" /> Track Order #{selectedOrder.id}
                    </h3>
                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-1">
                       Placed on {new Date(selectedOrder.createdAt).toLocaleString()}
                    </p>
                 </div>
                 <button onClick={() => setSelectedOrder(null)} className="p-3 hover:bg-gray-200 rounded-full transition-all">
                    <X className="h-6 w-6 text-gray-400" />
                 </button>
              </div>

              {/* Content Area */}
              <div className="flex-grow overflow-y-auto p-10">
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                    {/* Left: Timeline */}
                    <div className="lg:col-span-1 border-r border-gray-100 pr-10">
                       <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-8">Journey Status</h4>
                       <OrderTimeline status={selectedOrder.status} updatedAt={selectedOrder.updatedAt || selectedOrder.createdAt} />
                       
                       {selectedOrder.status === 'DELIVERED' && (
                         <div className="mt-10 p-6 bg-amber-50 rounded-3xl border border-amber-100 space-y-4">
                            <h5 className="font-black text-amber-900 text-sm flex items-center gap-2">
                               <RotateCcw className="h-4 w-4" /> Not Satisfied?
                            </h5>
                            <p className="text-xs text-amber-700 font-medium">Bhai, agar product mein koi dikkat hai toh aap return request daal sakte hain within 7 days.</p>
                            <button 
                              onClick={() => setIsReturnModalOpen(true)}
                              className="w-full py-3 bg-amber-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-amber-200"
                            >
                               Initiate Return
                            </button>
                         </div>
                       )}
                    </div>

                    {/* Right: Order Summary & Logistics */}
                    <div className="lg:col-span-2 space-y-10">
                       <div className="grid grid-cols-2 gap-8">
                          <div className="space-y-4">
                             <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <MapPin className="h-3 w-3" /> Shipping Address
                             </h4>
                             <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100 text-sm font-bold text-gray-700 leading-relaxed">
                                {user?.email}<br/>
                                Sector 45, Tech Park Phase 2<br/>
                                Bangalore, KA 560001, India
                             </div>
                          </div>
                          <div className="space-y-4">
                             <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <CreditCard className="h-3 w-3" /> Billing Info
                             </h4>
                             <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                                <p className="text-xs font-black text-gray-900 mb-2 uppercase tracking-widest">{selectedOrder.paymentMethod || 'CARD'}</p>
                                <div className="space-y-1 text-xs text-gray-500 font-medium">
                                   <div className="flex justify-between"><span>Subtotal:</span> <span className="font-bold text-gray-900">${selectedOrder.subtotal.toLocaleString()}</span></div>
                                   {selectedOrder.vipDiscountAmount ? <div className="flex justify-between text-blue-600"><span>VIP Discount:</span> <span>-${selectedOrder.vipDiscountAmount.toLocaleString()}</span></div> : null}
                                   {selectedOrder.coinsRedeemed ? <div className="flex justify-between text-orange-600"><span>Loyalty:</span> <span>-${(selectedOrder.coinsRedeemed * 0.1).toFixed(2)}</span></div> : null}
                                   <div className="flex justify-between"><span>Tax:</span> <span className="font-bold text-gray-900">${selectedOrder.taxAmount.toLocaleString()}</span></div>
                                   <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                                      <span className="text-sm font-black text-gray-900">Total:</span>
                                      <span className="text-sm font-black text-blue-600">${selectedOrder.total.toLocaleString()}</span>
                                   </div>
                                </div>
                             </div>
                          </div>
                       </div>

                       <div className="space-y-6">
                          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ordered Gadgets</h4>
                          <div className="space-y-3">
                             {selectedOrder.items?.map((item, idx) => (
                               <div key={idx} className="flex items-center gap-6 p-4 bg-white rounded-3xl border border-gray-100 hover:border-blue-100 transition-all">
                                  <div className="w-16 h-16 bg-gray-50 rounded-2xl overflow-hidden shrink-0">
                                     <img src={item.product.images[0]} className="w-full h-full object-cover" alt="" />
                                  </div>
                                  <div className="flex-grow">
                                     <p className="font-black text-gray-900">{item.product.name}</p>
                                     <div className="flex items-center gap-3 mt-1">
                                        {item.variant && <span className="text-[8px] font-black bg-gray-100 px-2 py-0.5 rounded uppercase tracking-widest"><Tag className="inline h-2 w-2 mr-1" /> {item.variant.name}</span>}
                                        <span className="text-xs font-bold text-gray-400">Qty: {item.quantity}</span>
                                     </div>
                                  </div>
                                  <div className="text-right">
                                     <p className="font-black text-gray-900">${(item.product.price * item.quantity).toLocaleString()}</p>
                                  </div>
                               </div>
                             ))}
                          </div>
                       </div>

                       <div className="p-8 bg-blue-50 rounded-[3rem] border border-blue-100 flex items-center justify-between">
                          <div className="flex items-center gap-6">
                             <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg">
                                <Package className="h-6 w-6" />
                             </div>
                             <div>
                                <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Seller Marketplace</p>
                                <h5 className="font-black text-gray-900">{stores.find(s => s.id === selectedOrder.storeId)?.name || 'Lynqar Merchant'}</h5>
                             </div>
                          </div>
                          <button className="text-blue-600 font-black text-xs uppercase tracking-widest hover:underline">Contact Support</button>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* Return Request Modal (Existing) */}
      {isReturnModalOpen && (
        <div className="fixed inset-0 z-[250] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-lg rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <div className="flex items-center gap-3">
                    <RotateCcw className="h-6 w-6 text-amber-600" />
                    <h3 className="text-xl font-black text-gray-900">Return Request</h3>
                 </div>
                 <button onClick={() => setIsReturnModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                    <X className="h-6 w-6 text-gray-400" />
                 </button>
              </div>
              <div className="p-10 space-y-8">
                 <div className="p-6 bg-amber-50 rounded-[2rem] border border-amber-100 flex gap-4">
                    <AlertCircle className="h-6 w-6 text-amber-600 shrink-0 mt-1" />
                    <p className="text-xs text-amber-800 font-medium leading-relaxed">
                       Lynqar handles returns with priority. Please explain the issue clearly. Once approved, the merchant will initiate the pick-up and refund.
                    </p>
                 </div>
                 
                 <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reason for Return</label>
                    <select 
                      value={returnReason}
                      onChange={e => setReturnReason(e.target.value)}
                      className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold focus:ring-2 focus:ring-amber-500"
                    >
                       <option value="">Select a reason...</option>
                       <option value="DEFECTIVE">Product is defective/damaged</option>
                       <option value="WRONG_ITEM">Received the wrong item</option>
                       <option value="NOT_AS_DESCRIBED">Item not as described</option>
                       <option value="QUALITY_ISSUE">Poor build quality</option>
                       <option value="CHANGED_MIND">No longer needed</option>
                    </select>
                 </div>

                 <button 
                   onClick={handleInitiateReturn}
                   disabled={!returnReason || isSubmittingReturn}
                   className="w-full py-6 rounded-[2rem] bg-amber-600 hover:bg-amber-700 text-white font-black text-lg shadow-2xl shadow-amber-100 active:scale-95 transition-all disabled:opacity-50"
                 >
                    {isSubmittingReturn ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : "Confirm Return Request"}
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default CustomerOrders;
